import {
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';


import {EmojiPickerService} from '../../../shared/services/emoji-picker.service';
import {Router, NavigationStart} from '@angular/router';
import {filter} from 'rxjs/operators';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

//interface for reaction data passed to this component
export interface Reaction {
  code: string;   // "1F44D", "1F389", etc. (hex Unicode)
  desc: string;   // label / description of the icon 
}

//interface for reaction selection passed to the main component
export interface ReactionSelection {
  emoji: string;               // actual emoji character
  code: string;                // hex code (same as Reaction.code)
  source: 'predefined' | 'custom';
  desc?: string;               // optional, for predefined
}

@Component({
    selector: 'app-reaction-picker',
    imports: [],
    templateUrl: './reaction-picker.component.html',
    styleUrls: ['./reaction-picker.component.scss']
})
export class ReactionPickerComponent implements OnDestroy {
  @Input() public reactions: Reaction[] = [
    {code: '1F44D', desc: 'Like'},        // 👍
    {code: '1F389', desc: 'Celebrate'},   // 🎉
    {code: '1F91D', desc: 'Support'},     // 🤝
    {code: '1F4A1', desc: 'Insightful'},  // 💡
    {code: '1F64F', desc: 'Appreciate'},  // 🙏
  ];
  
  //if we want to show Syncfusion full reaction popup to pick more, or not
  @Input() public enableCustomEmoji: boolean = true;
  @Input() public triggerDisplay: 'inline-block' | 'block' = 'inline-block';

  //Emoji chosen eiter from the quick pannel or from Syncfusion list
  @Output() public selection: EventEmitter<ReactionSelection> = new EventEmitter<ReactionSelection>();

  private readonly router: Router = inject(Router);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  @ViewChild('wrapper', {static: true})
  private wrapper!: ElementRef<HTMLDivElement>;

  //Generic trigger: can be any projected element
  @ViewChild('trigger', {static: true})
  private trigger!: ElementRef<HTMLElement>;

  @ViewChild('panel')
  private panel?: ElementRef<HTMLDivElement>;

  public isOpen: boolean = false;
  public panelStyle: Record<string, string> = {};
  private readonly emojiService:EmojiPickerService = inject(EmojiPickerService);

  public constructor() {
    this.router.events
      .pipe(
        filter((e: unknown): e is NavigationStart => e instanceof NavigationStart),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((): void => {
        this.closePanel(true);
      });
  }

  public ngOnDestroy(): void {
    //Just a safty measure to: ensure nothing remains in the overlay host after destruction
    this.closePanel(true);
  }

  private closePanel(removeFromDom: boolean): void {
    this.isOpen = false;

    if (!removeFromDom || !this.panel) {
      return;
    }

    const panelEl: HTMLDivElement = this.panel.nativeElement;

    //If it was portaled, it might survive component teardown unless we explicitly remove it
    if (panelEl.parentElement?.id === 'reaction-overlay-host') {
      panelEl.remove();
    }
  }

  /**
   * Needed to position the small emoji picker in the reaction-overlay-host container defined in emoji-picker.component which is placed inside app.component 
   * This will also make sure for the small emoji quick picker to be positions over the entire content.
   */
  private portalPanelToOverlayHost(): void {
    if (!this.panel) {
      return;
    }

    const host: HTMLElement | null = document.getElementById('reaction-overlay-host');
    if (!host) {
      return;
    }

    const panelEl: HTMLDivElement = this.panel.nativeElement;

    if (panelEl.parentElement !== host) {
      host.appendChild(panelEl);
    }
  }
  /**
   * Converts a Unicode code point (or sequence of code points) into a UTF-16 string representation (emoji).
   * Supports both single and multi-codepoint Unicode sequences (e.g., "1F468-200D-1F4BB").
   * @param code - The Unicode code point(s) as a string, separated by hyphens or spaces (e.g., "1F44D" or "1F468-200D-1F4BB")
   * @returns A string representing the emoji character(s)
   */
  private codeToEmoji(code: string): string {
    // support single or multi-codepoint ("1F468-200D-1F4BB" etc.)
    const parts: string[] = code.split(/[-\s]+/);
    const cps: number[] = parts.map(p => parseInt(p, 16));
    return String.fromCodePoint(...cps);
  }

  //public wrapper to use the converter in the template
  public getEmojiFromCode(code: string): string {
    return this.codeToEmoji(code);
  }

  /** Converts an emoji string into a hex Unicode sequence, e.g. 👍 -> "1F44D" */
  private emojiToHex(emoji: string): string {
    const codePoints: number[] = Array.from(emoji).map(c => c.codePointAt(0)!);
    return codePoints.map(cp => cp.toString(16).toUpperCase()).join('-');
  }

  public togglePanel(event?: MouseEvent): void {
     event?.stopPropagation();

    this.isOpen = !this.isOpen;

    if (this.isOpen) {
      requestAnimationFrame((): void => {
        this.portalPanelToOverlayHost();
        this.positionPanel();
      });
    } else {
      this.closePanel(true);
    }
  }

  public selectReaction(reaction: Reaction): void {
    this.selection.emit({
      emoji: this.codeToEmoji(reaction.code),
      code: reaction.code,
      source: 'predefined',
      desc: reaction.desc
    });
    this.isOpen = false;
  }

  public openEmojiPopup(event: MouseEvent): void {
    event.stopPropagation();

    if (!this.enableCustomEmoji) {
      return;
    }

    //The actual clicked + button (used only for container detection)
    const anchor: HTMLElement = event.currentTarget as HTMLElement;

    //Geometry anchor: the trigger span around the INITIATOR button
    const triggerEl: HTMLElement = this.trigger.nativeElement;
    const rect: DOMRect = triggerEl.getBoundingClientRect();
    this.emojiService.open(
      anchor,
      rect,
      (emoji: string): void => {
        const hex: string = this.emojiToHex(emoji);
        this.selection.emit({
          emoji,
          code: hex,
          source: 'custom',
        });
        this.isOpen = false; // close the small reaction panel
      }
    );
  }

  private positionPanel(): void {
    if (!this.panel) {
      return;
    }

    const triggerRect: DOMRect =
      this.trigger.nativeElement.getBoundingClientRect();
    const panelEl: HTMLDivElement = this.panel.nativeElement;

    const panelWidth: number = panelEl.offsetWidth;
    const panelHeight: number = panelEl.offsetHeight;

    const padding: number = 8;
    const vw: number = window.innerWidth;
    const vh: number = window.innerHeight;

    const spaceBelow: number = vh - triggerRect.bottom;
    const spaceAbove: number = triggerRect.top;
    const openAbove: boolean = spaceBelow < (panelHeight + padding) && spaceAbove > spaceBelow;

    let left: number = triggerRect.left + (triggerRect.width - panelWidth) / 2;
    let top: number = openAbove
      ? (triggerRect.top - panelHeight - padding)
      : (triggerRect.bottom + padding);

    left = Math.max(padding, Math.min(left, vw - panelWidth - padding));
    top = Math.max(padding, Math.min(top, vh - panelHeight - padding));

     this.applyPanelStyles(left, top);
  }

  private applyPanelStyles(left: number, top: number): void {
    if (!this.panel) {
      return;
    }

    const panelEl: HTMLDivElement = this.panel.nativeElement;

    panelEl.style.left = `${left}px`;
    panelEl.style.top = `${top}px`;
    panelEl.style.transform = 'none';
  }
  
  @HostListener('window:resize')
  @HostListener('window:scroll')
  public onWindowChange(): void {
    if (this.isOpen) {
      this.positionPanel();
    }
  }

  /**
   * Handles clicks outside the reaction picker to close the panel.
   * This method checks if the click occurred inside the trigger button or the panel itself.
   * If the click is outside both elements, it closes the panel and sets isOpen to false.
   * @param event - The mouse event triggered by a click on the document.
   */
  @HostListener('document:click', ['$event'])
  public onDocumentClick(event: MouseEvent): void {
    if (!this.isOpen) {
      return;
    }

    const target: HTMLElement = event.target as HTMLElement;

    const isInsideTrigger: boolean = this.trigger.nativeElement.contains(target);
    const isInsidePanel: boolean = !!this.panel && this.panel.nativeElement.contains(target);

    if (!isInsideTrigger && !isInsidePanel) {
      this.isOpen = false;
    }
  } 
}
